import Form from "@rjsf/core";
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { errorId, getTemplate, getUiOptions, type FieldTemplateProps, type GridTemplateProps, type FieldErrorProps } from "@rjsf/utils";
import { useLocale } from "../../i18n/LocaleProvider";
import { getWorkSettingsValidator } from "./generated/validators";
import {
  ActiveContextField,
  validateActiveContext,
  type HostContext,
  type ActiveContextStatus,
} from "./ActiveContextField";
import type { DocumentContextState } from "../../hooks/useDocumentContext";
import { resolveIndexView } from "../../lib/revitIndexState";
import { ActionState, IndexStatusView } from "../../components/chat/RevitIndexPanel";
import { renderWorkPrompt } from "./render-work-prompt";
import { explainedOptions, FieldInfo } from "./FieldInfo";
import { InfoIcon } from "../../components/skills/icons";
import type { useWorkSettings } from "./useWorkSettings";
import type { WorkSettingsSelection } from "../../../../shared/work-settings";
import "./settings.css";
import { ExecutionEstimate } from "./ExecutionEstimate";
import { isValidToolCallLimit, toolCallLimitEstimate, toolCallLimitField } from "./toolCallLimit";
import { CategoryCheckboxes } from "./CategoryCheckboxes";
import { FilePathWidget } from "./FilePathWidget";
function SettingsFieldTemplate(props: FieldTemplateProps) {
  const { id, label, children, errors, help, description, rawDescription, hidden, displayLabel, registry, schema, uiSchema } = props;
  const options = getUiOptions(uiSchema);
  const Wrap = getTemplate("WrapIfAdditionalTemplate", registry, options);
  if (hidden) return <div hidden>{children}</div>;
  const showLabel = displayLabel && options.widget !== "checkbox" && !!label;
  // With a label, the explanation lives in the bubble rather than under it.
  const info = showLabel && (!!rawDescription || explainedOptions(schema).length > 0);
  return <Wrap {...props}>
    {showLabel && <div className="work-settings-label-row">
      <label className="control-label" htmlFor={id}>{label}</label>
      <FieldInfo about={rawDescription} schema={schema} />
    </div>}
    {displayLabel && description && !info ? <div className="work-settings-description">{description}</div> : null}
    {children}{errors}{help}
  </Wrap>;
}

function SettingsFieldError({ errors = [], fieldPathId }: FieldErrorProps) {
  const message = errors.find(Boolean);
  return message ? <p id={errorId(fieldPathId)} className="work-settings-field-error" role="alert">{message}</p> : null;
}

function SectionIcon({ advanced = false }: { advanced?: boolean }) {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {advanced
      ? <><path d="M4 7h16M4 17h16M8 4v6M16 14v6" /></>
      : <><path d="M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01" /></>}
  </svg>;
}

function SettingsGridTemplate({ children, column: _column, collapsible, title, ...props }: GridTemplateProps) {
  if (!collapsible) return <div {...props}>{children}</div>;
  return <details {...props}>
    <summary className="work-settings-section-toggle">
      <span className="work-settings-section-label"><SectionIcon advanced />{title}</span>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg>
    </summary>
    <div className="work-settings-section-content">{children}</div>
  </details>;
}

function SectionHeading({ title }: { title: string }) {
  return <h3 className="work-settings-section-title">{title}</h3>;
}

/**
 * The start form, drawn as the workflow's own Setup section (`docs/proposals/workflows.md`):
 * the view names the workflow, so the panel has no header or chooser of its own — the
 * workflow was chosen on its card (the chooser PR #210 drew went with the Custom task
 * card, owner 2026-10-10) — only the form, `controls`, `notice` and a Start button.
 */
/** The start button's own label: root `ui:submitButtonOptions.submitText`, localised like the form; null for the generic one. */
function submitText(uiSchema: Record<string, unknown> | undefined): string | null {
  const text = (uiSchema?.["ui:submitButtonOptions"] as { submitText?: unknown } | undefined)?.submitText;
  return typeof text === "string" && text ? text : null;
}

export function WorkSettingsPanel({
  state,
  workflowDescription = "",
  historical,
  controls = null,
  notice = null,
  disabled,
  host,
  documentContext,
  onApply,
}: {
  state: ReturnType<typeof useWorkSettings>;
  workflowDescription?: string;
  historical: boolean;
  /** The model and reasoning-effort pickers, above Start. */
  controls?: ReactNode;
  /**
   * What pressing Start consents to — the workflow's forced automatic mode, in
   * words — behind an info glyph beside the model picker, shown on hover or focus
   * (owner, 2026-10-10: a glyph with the text, not a sentence, not a checkbox).
   */
  notice?: string | null;
  disabled: boolean;
  host: HostContext;
  /** The selected Revit's index, shared with the Revit Database panel. */
  documentContext: DocumentContextState;
  onApply: (text: string, selection: WorkSettingsSelection) => void;
}) {
  const formId = useId();
  const { locale, t } = useLocale();
  const language = locale === "zh" ? "zh-TW" : "en";
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const [contextStatus, setContextStatus] = useState<Record<string, ActiveContextStatus>>({});
  const [contextReload, setContextReload] = useState(0);
  const onContextStatus = useCallback((field: string, status: ActiveContextStatus) => {
    setContextStatus(previous => ({ ...previous, [field]: {
      ...status, error: status.loading ? previous[field]?.error ?? "" : status.error,
    } }));
  }, []);
  const snapshot = state.snapshot;
  const definition = historical
    ? snapshot?.definition
    : state.definitions.find((item) => item.workflowId === state.workflowId);
  const variant = definition?.locales[language];
  const validator = variant ? getWorkSettingsValidator(variant) : null;
  const workflowId = historical ? snapshot?.selection.workflowId ?? "" : state.workflowId;
  const formData = historical ? snapshot?.selection.formData ?? {} : state.formData;
  // The allowance's bounds and default are the start form's own field (intake/schema.json).
  const budget = toolCallLimitField(variant?.schema);
  const executionBudget = budget !== null;
  const budgetValue = formData.toolCallLimit ?? budget?.default;
  const budgetInvalid = budget !== null && !isValidToolCallLimit(budgetValue, budget);
  // The workflow's own time estimate: `ui:options.estimate` on the field, localised like the form.
  const budgetEstimate = toolCallLimitEstimate(variant?.uiSchema);
  const contextFields = Object.entries(variant?.uiSchema ?? {})
    .filter(([, config]) => config?.["ui:field"] === "ActiveContext").map(([key]) => key);
  // The index's state is the Revit Database panel's, rung for rung: building, failed,
  // no document, nothing built. Only once it is there do the fields' own checks speak.
  const needsIndex = !historical && contextFields.length > 0;
  const connected = !!host.sessionId && !!host.revitVersion;
  const indexView = needsIndex && connected ? resolveIndexView({
    status: documentContext.status,
    keysLoaded: documentContext.keysLoaded,
    keyCount: documentContext.keys.length,
    loading: documentContext.loadingKeys && !documentContext.keysLoaded,
    showRows: false,
    rebuilding: documentContext.rebuilding,
  }) : null;
  const indexKind = indexView && indexView.kind !== "rows" && indexView.kind !== "types" && indexView.kind !== "loading" ? indexView.kind : null;
  const indexBlocked = needsIndex && (!connected || indexKind !== null);
  const contextLoading = documentContext.rebuilding || indexView?.kind === "loading" ||
    (!historical && contextFields.some(key => contextStatus[key]?.loading));
  const contextError = needsIndex
    ? error || contextFields.map(key => contextStatus[key]?.error).find(Boolean) || "" : "";
  const blocked = indexBlocked || !!contextError;
  // As the panel's refresh: rebuild when the plugin can, otherwise re-read the file.
  const rebuildOrRefresh = () => {
    setError("");
    const done = documentContext.status?.canRebuild ? documentContext.rebuild() : documentContext.refresh();
    void Promise.resolve(done).then(() => { if (alive.current) setContextReload(value => value + 1); });
  };
  useEffect(() => { setContextStatus({}); setError(""); }, [workflowId, host.orgId, host.sessionId, host.revitVersion]);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const applyContext = JSON.stringify([
    host.orgId,
    host.sessionId,
    host.revitVersion,
    definition?.revision,
    language,
    state.formData,
  ]);
  const currentApplyContext = useRef(applyContext);
  currentApplyContext.current = applyContext;
  return (
    <aside
      className={`work-settings-panel${historical ? " is-readonly" : ""}`}
      aria-label={t("workSettings.title")}
    >
      <div className={`work-settings-content${blocked ? " is-context-blocked" : ""}`}>
      <div className="work-settings-body" aria-hidden={blocked ? true : undefined} {...(blocked ? { inert: "" } : {})}>
        {state.loading && <p role="status">{t("common.loading")}</p>}
        {state.error && <p role="alert">{state.error}</p>}
        {historical && !snapshot && !state.loading && <p>{t("workSettings.noHistory")}</p>}
        {!historical && state.workflowId && !definition && !state.loading && (
          <p role="alert">
            {t("workSettings.unavailable")}
          </p>
        )}
        {variant && !validator && (
          <p role="alert">
            {t("workSettings.incompatible")}
          </p>
        )}
        {variant && validator && definition && (
          <Form
            id={formId}
            templates={{ FieldTemplate: SettingsFieldTemplate, GridTemplate: SettingsGridTemplate, FieldErrorTemplate: SettingsFieldError }}
            focusOnFirstError
            schema={variant.schema}
            uiSchema={historical && executionBudget ? { ...variant.uiSchema, toolCallLimit: { "ui:widget": "updown" } } : variant.uiSchema}
            validator={validator}
            formData={formData}
            fields={{ ActiveContext: ActiveContextField }}
            widgets={{ filePath: FilePathWidget, checkboxes: CategoryCheckboxes }}
            formContext={{ ...host, workflowId, formData, documentContext, contextReload, onContextStatus, layoutGridLookupMap: { SectionHeading } }}
            readonly={historical}
            disabled={!historical && (disabled || checking || blocked)}
            onError={() => {
              // Reveal collapsed fields before RJSF focuses the first invalid input.
              document.getElementById(formId)?.querySelectorAll("details").forEach((section) => {
                // Widget disclosures have their field error beside them, outside the details.
                const field = section.classList.contains("work-settings-category-disclosure") ? section.closest(".form-group") : null;
                if (section.querySelector(".work-settings-field-error") || field?.querySelector(".work-settings-field-error")) section.open = true;
              });
            }}
            noHtml5Validate
            showErrorList={false}
            transformErrors={(errors) => errors.map((issue) => {
              const field = issue.title || t("workSettings.validation.field");
              let message = t("workSettings.validation.value");
              if (issue.name === "required" || (issue.name === "pattern" && issue.params?.pattern === "\\S")) {
                message = t("workSettings.validation.required", { field });
              } else if (issue.name === "maxLength") {
                message = t("workSettings.validation.maxLength", { limit: issue.params.limit });
              } else if (issue.name === "minLength") {
                message = t("workSettings.validation.minLength", { limit: issue.params.limit });
              } else if (issue.name === "minItems") {
                message = t("workSettings.validation.minItems", { limit: issue.params.limit });
              } else if (issue.name === "enum" || issue.name === "const") {
                message = t("workSettings.validation.option");
              }
              return { ...issue, message, stack: message };
            })}
            onChange={(event) => { if (!historical) state.setFormData(event.formData ?? {}); }}
            onSubmit={(event) => {
              if (historical || budgetInvalid) return;
              setChecking(true);
              setError("");
              void validateActiveContext(
                event.formData ?? {},
                variant.uiSchema,
                host,
                t,
              )
                .then(() => {
                  if (
                    alive.current &&
                    currentApplyContext.current === applyContext
                  )
                    onApply(
                      renderWorkPrompt(variant, event.formData ?? {}),
                      {
                        workflowId: definition.workflowId,
                        revision: definition.revision,
                        locale: language,
                        formData: event.formData ?? {},
                      },
                    );
                })
                .catch((error) => {
                  if (alive.current && currentApplyContext.current === applyContext)
                    setError(String(error.message ?? error));
                })
                .finally(() => { if (alive.current) setChecking(false); });
            }}
          >
            <></>
          </Form>
        )}
        {error && !blocked && <p role="alert">{error}</p>}
      </div>
      {blocked && <div className="work-settings-context-blocker">
        {!connected ? <ActionState title={t("workflows.noRevit")} description={workflowDescription}
          icon={<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 4h6v6H4zM14 14h6v6h-6zM10 7h4a3 3 0 0 1 3 3v4" />
          </svg>} />
          : indexKind ? <IndexStatusView kind={indexKind} status={documentContext.status} onAction={rebuildOrRefresh} />
          : <ActionState title={t("workSettings.context.blocked")} description={contextError} action={t("revitIndex.refresh")} onAction={rebuildOrRefresh} />}
      </div>}
      </div>
      {variant && validator && (
        <footer className={`work-settings-footer${executionBudget ? " work-settings-execution-footer" : ""}`}>
          {historical ? <p className="work-settings-readonly-hint">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="10" width="14" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>
            {t("workSettings.readonlyHint")}
          </p> : <>
            {budget ? <ExecutionEstimate value={budgetValue} field={budget} controls={controls} notice={notice}
              about={budget.description} estimate={budgetEstimate}
              disabled={disabled || checking || state.loading || blocked}
              onChange={value => state.setFormData(previous => ({ ...previous, toolCallLimit: value }))} />
            : controls || notice ? <div className="work-settings-run-controls">
              {controls}
              {notice ? <span className="info-tip work-settings-mode-info" tabIndex={0} role="note" aria-label={notice}>
                <InfoIcon size={14} />
                <span className="info-tip-bubble" role="tooltip">{notice}</span>
              </span> : null}
            </div> : null}
            <button type="submit" form={formId} className="btn primary work-settings-start" disabled={disabled || checking || state.loading || contextLoading || blocked || budgetInvalid}>
              {submitText(variant.uiSchema) ?? t("workflows.start")}
            </button>
          </>}
        </footer>
      )}
    </aside>
  );
}
